/**
 * User Validation Utilities
 *
 * Functions for validating user input before creating/updating users.
 */

import { VALIDATION } from '../constants';

/**
 * Validate email format
 *
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email is valid
 */
export function isValidEmail(email) {
  if (!email) return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= VALIDATION.EMAIL_MAX_LENGTH;
}

/**
 * Validate first name
 *
 * @param {string} firstName - First name to validate
 * @returns {boolean} True if first name is valid
 */
export function isValidFirstName(firstName) {
  if (!firstName) return false;

  const trimmed = firstName.trim();
  return (
    trimmed.length >= VALIDATION.FIRST_NAME_MIN_LENGTH &&
    trimmed.length <= VALIDATION.FIRST_NAME_MAX_LENGTH
  );
}

/**
 * Validate last name
 *
 * @param {string} lastName - Last name to validate
 * @returns {boolean} True if last name is valid
 */
export function isValidLastName(lastName) {
  if (!lastName) return false;

  const trimmed = lastName.trim();
  return (
    trimmed.length >= VALIDATION.LAST_NAME_MIN_LENGTH &&
    trimmed.length <= VALIDATION.LAST_NAME_MAX_LENGTH
  );
}

/**
 * Get validation error message for email
 *
 * @param {string} email - Email to validate
 * @returns {string|null} Error message or null if valid
 */
export function getEmailError(email) {
  if (!email) return 'Email is required';
  if (!isValidEmail(email)) return 'Please enter a valid email address';
  if (email.length > VALIDATION.EMAIL_MAX_LENGTH) {
    return `Email must be ${VALIDATION.EMAIL_MAX_LENGTH} characters or less`;
  }
  return null;
}

/**
 * Get validation error message for first name
 *
 * @param {string} firstName - First name to validate
 * @returns {string|null} Error message or null if valid
 */
export function getFirstNameError(firstName) {
  if (!firstName || !firstName.trim()) return 'First name is required';
  const trimmed = firstName.trim();
  if (trimmed.length < VALIDATION.FIRST_NAME_MIN_LENGTH) {
    return `First name must be at least ${VALIDATION.FIRST_NAME_MIN_LENGTH} character`;
  }
  if (trimmed.length > VALIDATION.FIRST_NAME_MAX_LENGTH) {
    return `First name must be ${VALIDATION.FIRST_NAME_MAX_LENGTH} characters or less`;
  }
  return null;
}

/**
 * Get validation error message for last name
 *
 * @param {string} lastName - Last name to validate
 * @returns {string|null} Error message or null if valid
 */
export function getLastNameError(lastName) {
  if (!lastName || !lastName.trim()) return 'Last name is required';
  const trimmed = lastName.trim();
  if (trimmed.length < VALIDATION.LAST_NAME_MIN_LENGTH) {
    return `Last name must be at least ${VALIDATION.LAST_NAME_MIN_LENGTH} character`;
  }
  if (trimmed.length > VALIDATION.LAST_NAME_MAX_LENGTH) {
    return `Last name must be ${VALIDATION.LAST_NAME_MAX_LENGTH} characters or less`;
  }
  return null;
}
